import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Pause, Check, ChevronLeft, Loader2 } from "lucide-react";
import { api, useAuth } from "@/hooks/useAuth";

/**
 * VoiceAudition — development-only tool for shortlisting Krishna's voice.
 * Route: /app/talk/voices
 *
 * Does NOT modify the voice pipeline. Only lists candidate voice_ids, lets you
 * preview them against two scripts, and (optionally) commits the selected one
 * to Mongo (`kalki_config.voice`) which the live `/api/kalki/tts` endpoint reads.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const INDIGO = "#5D00FF";
const FUCHSIA = "#FF2E9A";
const LIME = "#CFFF04";
const GRAPHITE = "#1C1C1C";

const SCRIPTS = {
  calm: `Hello. I am KALKI, your friend. Life does not always reveal the entire path at once. Sometimes, all you need is the courage to see the next step clearly. Tell me, what is on your mind today?`,
  wisdom: `You are afraid because this decision matters to you. But do not let fear make the decision in your place. Understand what is in your control, choose your karma carefully, and then walk forward with courage. I am with you.`,
};

export default function VoiceAudition() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [script, setScript] = useState("calm");
  const [busyId, setBusyId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [committing, setCommitting] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [error, setError] = useState("");

  const audioRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) navigate("/app", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/kalki/voices");
        setList(r.data?.shortlist || []);
        setActive(r.data?.active_voice_id || null);
      } catch (e) { setError("Could not load voice shortlist."); }
    })();
  }, []);

  // Keep ONE persistent audio element — same pattern as the working Talk hook.
  const ensureAudio = () => {
    if (!audioRef.current) {
      const el = new Audio();
      el.preload = "auto";
      el.playsInline = true;
      el.volume = 1;
      audioRef.current = el;
    }
    return audioRef.current;
  };

  const play = async (voice_id) => {
    setError("");
    if (busyId) return;
    setBusyId(voice_id);
    try {
      // Pause anything already playing
      const el = ensureAudio();
      try { el.pause(); } catch (_) {}
      // Fetch audio
      const r = await fetch(`${BACKEND_URL}/api/kalki/voices/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ voice_id, text: SCRIPTS[script] }),
      });
      if (!r.ok) throw new Error(`TTS ${r.status}`);
      const type = r.headers.get("content-type") || "";
      const blob = await r.blob();
      if (!type.startsWith("audio/") || blob.size === 0) throw new Error("empty blob");
      const url = URL.createObjectURL(blob);
      el.src = url;
      el.onplaying = () => setPlayingId(voice_id);
      el.onended = () => { setPlayingId(null); try { URL.revokeObjectURL(url); } catch (_) {} };
      el.onerror = () => setPlayingId(null);
      await el.play();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusyId(null);
    }
  };

  const commitVoice = async (voice_id) => {
    try {
      setCommitting(voice_id);
      setError("");
      const r = await api.post("/kalki/voices/select", { voice_id });
      setActive(r.data?.voice_id || voice_id);
      setCommitted(voice_id);
      setTimeout(() => setCommitted(null), 2000);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not select voice.");
    } finally {
      setCommitting(null);
    }
  };

  return (
    <div className="min-h-[100dvh] text-white" style={{ background: GRAPHITE }} data-testid="voice-audition">
      <div className="max-w-[720px] mx-auto px-6 py-8">
        <button onClick={() => navigate("/app/talk")} className="inline-flex items-center gap-1 text-[11px] tracking-[0.3em] opacity-70 hover:opacity-100">
          <ChevronLeft className="w-3 h-3" /> BACK TO TALK
        </button>

        <h1 className="text-[14px] tracking-[0.4em] opacity-80 mt-6">KALKI VOICE AUDITION</h1>
        <p className="text-[12px] opacity-55 mt-1">Pick KALKI's voice. The pipeline is locked — this only changes the voice_id.</p>

        {/* Script toggle */}
        <div className="mt-6 flex items-center gap-2">
          {[
            { id: "calm",   label: "SCRIPT 01 · Introduction" },
            { id: "wisdom", label: "SCRIPT 02 · Wisdom" },
          ].map((s) => (
            <button
              key={s.id}
              data-testid={`audition-script-${s.id}`}
              onClick={() => setScript(s.id)}
              className="rounded-full px-4 py-2 text-[10px] tracking-[0.3em]"
              style={{
                background: script === s.id ? "white" : "rgba(255,255,255,0.06)",
                color: script === s.id ? GRAPHITE : "#F8FAFC",
                border: `1px solid ${script === s.id ? "white" : "rgba(255,255,255,0.14)"}`,
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <blockquote
          className="mt-4 rounded-xl px-4 py-3 text-[13px] leading-[1.6]"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#D9D9E0" }}
          data-testid="audition-script-preview"
        >
          {SCRIPTS[script]}
        </blockquote>

        {/* Voice cards */}
        <ul className="mt-8 space-y-3">
          {list.map((v, i) => {
            const isActive = v.voice_id === active;
            const isPlaying = playingId === v.voice_id;
            const isLoading = busyId === v.voice_id;
            const isCommitting = committing === v.voice_id;
            const justCommitted = committed === v.voice_id;
            return (
              <li
                key={v.voice_id}
                data-testid={`voice-row-${i + 1}`}
                className="flex items-center gap-4 rounded-2xl px-4 py-4"
                style={{
                  background: isActive ? `${INDIGO}18` : "rgba(255,255,255,0.04)",
                  border: `1px solid ${isActive ? INDIGO : "rgba(255,255,255,0.08)"}`,
                }}
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-semibold"
                  style={{ background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`, color: "white" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-medium">{v.name}</span>
                    {isActive && (
                      <span className="text-[9px] px-2 py-0.5 rounded-full tracking-[0.28em]"
                            style={{ color: LIME, border: `1px solid ${LIME}66`, background: `${LIME}10` }}>
                        ACTIVE
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] opacity-70">{v.label} · {v.age} · {v.accent}</div>
                  {v.descriptive && (
                    <div className="text-[10px] mt-0.5" style={{ color: FUCHSIA, letterSpacing: "0.1em" }}>
                      {v.descriptive}
                    </div>
                  )}
                  {v.notes && (
                    <div className="text-[11px] opacity-55 mt-1 leading-relaxed">{v.notes}</div>
                  )}
                  <div className="text-[9px] opacity-40 font-mono mt-0.5 truncate">{v.voice_id}</div>
                </div>

                <button
                  data-testid={`voice-play-${i + 1}`}
                  onClick={() => play(v.voice_id)}
                  disabled={isLoading}
                  className="w-11 h-11 rounded-full flex items-center justify-center transition disabled:opacity-50"
                  style={{
                    background: isPlaying ? FUCHSIA : "rgba(255,255,255,0.08)",
                    border: `1px solid ${isPlaying ? FUCHSIA : "rgba(255,255,255,0.18)"}`,
                  }}
                  aria-label="Play preview"
                >
                  {isLoading
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : isPlaying
                      ? <Pause className="w-4 h-4" />
                      : <Play className="w-4 h-4 translate-x-[1px]" />}
                </button>

                <button
                  data-testid={`voice-select-${i + 1}`}
                  onClick={() => commitVoice(v.voice_id)}
                  disabled={isCommitting || isActive}
                  className="shrink-0 rounded-full px-4 py-2.5 text-[10px] tracking-[0.26em] font-medium disabled:opacity-50"
                  style={{
                    background: justCommitted ? LIME : isActive ? "rgba(255,255,255,0.04)" : "white",
                    color: isActive ? "rgba(255,255,255,0.65)" : GRAPHITE,
                    border: isActive ? "1px solid rgba(255,255,255,0.14)" : "1px solid white",
                  }}
                >
                  {isCommitting
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : justCommitted
                      ? <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5" /> SAVED</span>
                      : isActive ? "IN USE" : "USE THIS AS KALKI"}
                </button>
              </li>
            );
          })}
        </ul>

        {error && (
          <div className="mt-6 rounded-xl px-4 py-3 text-[12px]"
               style={{ background: `${FUCHSIA}1a`, border: `1px solid ${FUCHSIA}66`, color: "#FFE1EC" }}>
            {error}
          </div>
        )}

        <p className="text-[10px] opacity-40 mt-10 tracking-[0.2em]">
          Pipeline unchanged. Only `kalki_config.voice.voice_id` is updated. The next `/api/kalki/tts` call picks it up automatically.
        </p>
      </div>
    </div>
  );
}
